import { Treatment } from "@/services/backend/rlt/treatments/types/treatment";
import { SegmentKind } from "@/services/backend/rlt/treatments/types/segmentKind";
import {
  FIRST_COLUMN_END,
  LINE_LENGTH,
  POSITION_COUNT_LENGTH,
  POSITION_PRICE_LENGTH,
  POSITION_TITLE_LENGTH,
  SECOND_COLUMN_END,
  THIRD_COLUMN_END,
} from "@/services/backend/connect/components/string-builder-helpers";
import t from "@/lang/lang";
import { useMemo } from "react";
import { useRltPricingShowQuery } from "@/services/backend/rlt/pricing/api/service";
import { EXECUTION_AREA_KEYS } from "@/routes/processes/[processId]/rlt/work-orders/execution/[workOrderId]/treatment/_components/add-extra-work-during-execution-drop-down";
import {
  AccessPanelData,
  Component,
  ExtraHoursData,
  Kind,
} from "@/services/backend/rlt/treatments/types/component";

const euroFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  currencyDisplay: "code",
});

export function useRltSegmentPrices(treatment?: Treatment): string {
  return useMemo(() => {
    if (!treatment) return "";
    return rltSegmentPrices(treatment);
  }, [treatment]);
}

function rltSegmentPrices(treatment: Treatment): string {
  const segments: string[] = treatment.segments
    .map((segment) => {
      if (segment.finalPrice === 0) {
        return "";
      }
      let line: string = "";
      // First Column: Segment Number
      line = segmentNumber(treatment, segment.segmentKind);
      line = line.padEnd(FIRST_COLUMN_END);
      // Second Column is ignored, "count" makes no sense for segments
      line = line.padEnd(SECOND_COLUMN_END);
      // Third Column: Segment Kind / Name
      line += t(segment.segmentKind);
      line = line.padEnd(THIRD_COLUMN_END);
      // Fourth Column: Price
      let price = euroFormatter.format(segment.finalPrice);
      price = price.padStart(POSITION_PRICE_LENGTH);
      line += price;

      if (
        segment.segmentKind === SegmentKind.Extras &&
        hasExecutionArea(treatment)
      ) {
        const subLines: string[] = [];
        subLines.push(
          "davon zusätzlich bei Ausführung:"
            .padEnd(POSITION_TITLE_LENGTH)
            .padStart(THIRD_COLUMN_END),
        );
        subLines.push(
          ...positionsInExecutionArea(treatment).map((pos) => {
            if (pos.finalPrice === 0) {
              return "";
            }
            let subLine = "";
            // First Column: empty
            subLine = subLine.padEnd(FIRST_COLUMN_END);
            // Second Column: empty
            subLine += `${quantity(pos.component)}  `.padStart(
              POSITION_COUNT_LENGTH,
            );
            // Third Column: Position Title
            subLine += t(pos.component.kind).padEnd(
              POSITION_TITLE_LENGTH - POSITION_PRICE_LENGTH,
            );
            // Fourth Column: Price
            let posPrice = euroFormatter.format(pos.finalPrice);
            posPrice = posPrice.padStart(POSITION_PRICE_LENGTH);
            subLine += posPrice;

            return subLine;
          }),
        );

        line = [line, ...subLines].join("\n");
      }

      return line;
    })
    .filter((line) => line !== "");

  return segments.join("\n");
}

function segmentNumber(treatment: Treatment, segmentKind: SegmentKind): string {
  const pos = treatment.positions.find((p) => p.segmentKind === segmentKind);
  if (!pos) {
    return "";
  }
  return `${pos.number.split(".")[0]}.0`;
}

function hasExecutionArea(treatment: Treatment) {
  return treatment.positions.some((p) => EXECUTION_AREA_KEYS.includes(p.area));
}

function positionsInExecutionArea(treatment: Treatment) {
  return treatment.positions.filter((p) =>
    EXECUTION_AREA_KEYS.includes(p.area),
  );
}

function quantity(component: Component): string {
  const { kind, data } = component;

  switch (kind) {
    case Kind.AccessPanel:
      return `${(data as AccessPanelData).count} Stk`;
    case Kind.ExtraHours:
      return `${(data as ExtraHoursData).hours} Std`;
    case Kind.FreeText:
      return "";
    default:
      return "";
  }
}

export function useRltPricing(pricingId: string): string {
  const { data: pricing } = useRltPricingShowQuery({ id: pricingId });
  if (!pricing) {
    return "";
  }

  // Treatment Price
  const { treatmentPrice } = pricing;
  const lines: string[] = [];
  lines.push(separatorLine);
  // Base Price Line
  let basePriceLine = "Grundbehandlung";
  basePriceLine = basePriceLine.padEnd(THIRD_COLUMN_END);
  basePriceLine += euroFormatter
    .format(treatmentPrice.basePrice)
    .padStart(POSITION_PRICE_LENGTH);
  lines.push(basePriceLine);
  // Discounts
  if (
    treatmentPrice.relativeDiscount.active &&
    treatmentPrice.relativeDiscount.percentage > 0
  ) {
    let discountLine = `- Rabatt ${treatmentPrice.relativeDiscount.percentage}%`;
    discountLine = discountLine.padEnd(THIRD_COLUMN_END);
    const discountAmount =
      (treatmentPrice.relativeDiscount.percentage / 100) *
      treatmentPrice.basePrice;
    discountLine += `- ${euroFormatter.format(discountAmount)}`.padStart(
      POSITION_PRICE_LENGTH,
    );
    lines.push(discountLine);
  }
  if (
    treatmentPrice.absoluteDiscount.active &&
    treatmentPrice.absoluteDiscount.amount > 0
  ) {
    let discountLine = `- Sonderrabatt`;
    discountLine = discountLine.padEnd(THIRD_COLUMN_END);
    discountLine += `- ${euroFormatter.format(
      treatmentPrice.absoluteDiscount.amount,
    )}`.padStart(POSITION_PRICE_LENGTH);
    lines.push(discountLine);
  }
  if (treatmentPrice.basePrice !== treatmentPrice.finalPrice) {
    lines.push(separatorLine);
    let finalPriceLine = "Grundbehandlung abzüglich Rabatte";
    finalPriceLine = finalPriceLine.padEnd(THIRD_COLUMN_END);
    finalPriceLine += euroFormatter
      .format(treatmentPrice.finalPrice)
      .padStart(POSITION_PRICE_LENGTH);
    lines.push(finalPriceLine);
  }

  // Post-Execution Reduction
  if (treatmentPrice.postExecutionReduction.amount > 0) {
    let reductionLine = `- ${treatmentPrice.postExecutionReduction.label}`;
    reductionLine = reductionLine.padEnd(THIRD_COLUMN_END);
    reductionLine += `- ${euroFormatter.format(
      treatmentPrice.postExecutionReduction.amount,
    )}`.padStart(POSITION_PRICE_LENGTH);
    lines.push(reductionLine);
  }

  // Incidental Costs
  const { incidentalCost } = pricing;
  let incidentalCostLine =
    "Geräte, Werkzeuge und Betriebsmittel, sowie An- und Abfahrtskosten";
  incidentalCostLine = incidentalCostLine.padEnd(THIRD_COLUMN_END);
  incidentalCostLine += euroFormatter
    .format(incidentalCost.totalAmount)
    .padStart(POSITION_PRICE_LENGTH);
  lines.push(incidentalCostLine);

  // Total
  lines.push(separatorLine);
  let totalLine = "Gesamtpreis";
  totalLine = totalLine.padEnd(THIRD_COLUMN_END);
  totalLine += euroFormatter
    .format(pricing.totalPrice)
    .padStart(POSITION_PRICE_LENGTH);
  lines.push(totalLine);

  return lines.join("\n");
}

const separatorLine = "-".repeat(LINE_LENGTH);
